import React from "react";
import { Video, Hand, CheckCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import moment from "moment-timezone";
import "./Style.css";
import { registerTrainingEvent } from "../../../services/calendar/Calendar";
import LoadingSpinner from "./LoadingSpinner";
import { useCalendarClient } from "../../../context/ApiContext";

export interface CalendarEventDetail {
  Id: any;
  Title: string;
  StartTime?: string | Date;
  EndTime?: string | Date;
  Location?: string;
  DurationInMinutes?: string | number;
  Category?: string;
  MeetingLink?: string;
  Color?: string | null;
  IsAllDay?: boolean;
  PBSLiveEvent?: boolean;
  IsBroadCast?: boolean;
  DetailsJSON?: any;
  Description?: any;
  UTCDiff?: number;
  UTCDiffEndTime?: number;
}

export interface EventDetailCardProps {
  event: CalendarEventDetail;
  loginName: string;
  onJoinMeeting?: (event: CalendarEventDetail) => void;
  onBusinessForm?: (event: CalendarEventDetail) => void;
  onRefreshEvents?: () => void;
}

const formatMonthDay = (value?: string | Date) => {
  if (!value) return { month: "", day: "" };
  const d = typeof value === "string" ? new Date(value) : value;
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate().toString();
  return { month, day };
};

const EventDetailCard: React.FC<EventDetailCardProps> = ({
  event,
  loginName,
  onRefreshEvents,
  onJoinMeeting,
  onBusinessForm,
}) => {
  const calendarClient = useCalendarClient();
  const { month, day } = formatMonthDay(event?.StartTime);
  const detailsJSON = JSON.parse(event.DetailsJSON);
  const [loading, setLoading] = React.useState(false);
  const handleRegisterNowClick = async () => {
    setLoading(true);
    try {
      await registerTrainingEvent(
        { LoginName: loginName, TrainingEventId: event.Id },
        calendarClient,
      );
      if (onRefreshEvents) onRefreshEvents();
    } catch (error) {
      console.error("Error registering for training event:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative rounded-lg border border-border bg-background shadow-sm p-4 pr-10 flex flex-col gap-2">
      {/* Corner Month/Day badge */}
      <div className="event-detail-date-badge">
        <span className="monthTextStyle">{month}</span>
        <span className="dayTextStyle">{day}</span>
      </div>

      <span className="eventTitleStyle">{event?.Title}</span>
      <span className="eventTimeLabelStyle">Event Time:</span>
      <span className="eventTimeValueStyle">{`${moment
        .utc(event?.StartTime)
        .local()
        .format("MM/DD/YYYY hh:mm A")} - ${moment
        .utc(event?.EndTime)
        .local()
        .format("MM/DD/YYYY hh:mm A")}`}</span>
      {event?.Location && (
        <span className="eventLocationStyle">
          <span style={{ fontWeight: 600, fontSize: 15 }}>Location:</span>{" "}
          {event.Location}
        </span>
      )}
      {event?.DurationInMinutes && (
        <span className="eventDurationStyle">
          <span style={{ fontWeight: 600, fontSize: 15 }}>Duration:</span>{" "}
          {event.DurationInMinutes}
        </span>
      )}
      {event.Description && (
        <span className="eventDetailsStyle">
          Details:
          <div
            className="eventDescriptionStyle"
            dangerouslySetInnerHTML={{ __html: event.Description }}
          />
        </span>
      )}
      {event.DetailsJSON && (
        <>
          <span className="eventTrainerLabelStyle">Trainer:</span>
          <div className="flex items-center gap-3 mt-2">
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={`https://sharepoint.teampbs.com/_layouts/15/userphoto.aspx?size=S&accountname=${detailsJSON.trainerorhostLogin}`}
              />
              <AvatarFallback>
                {detailsJSON?.trainerorhostName?.charAt(0) ?? "?"}
              </AvatarFallback>
            </Avatar>
            <span className="font-semibold text-foreground text-sm">
              {detailsJSON?.trainerorhostName}
            </span>
          </div>
        </>
      )}

      <div className="flex justify-end items-center gap-2 mt-2">
        {event.MeetingLink && (
          <Button
            size="sm"
            disabled={loading}
            onClick={() => onJoinMeeting && onJoinMeeting(event)}
            className="rounded text-xs px-2 py-1"
          >
            <Video className="h-4 w-4 mr-1" />
            Join Now
          </Button>
        )}
        {event.Category === "Other" && event.MeetingLink && onBusinessForm && (
          <Button
            size="sm"
            disabled={loading}
            onClick={() => onBusinessForm && onBusinessForm(event)}
            className="rounded text-xs px-2 py-1"
          >
            <FileText className="h-4 w-4 mr-1" />
            View Form
          </Button>
        )}
        {event.DetailsJSON &&
          detailsJSON.isRegistered === 0 &&
          (loading ? (
            <div className="mx-10">
              <LoadingSpinner spinnerSize="small" label="" />
            </div>
          ) : (
            <Button
              size="sm"
              onClick={handleRegisterNowClick}
              disabled={loading}
              className="rounded text-xs px-2 py-1"
            >
              <Hand className="h-4 w-4 mr-1" />
              Register Now
            </Button>
          ))}
        {event.DetailsJSON && detailsJSON.isRegistered === 1 && (
          <span className="inline-flex items-center text-green-600 font-semibold text-sm">
            <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
            Registered
          </span>
        )}
      </div>
    </div>
  );
};

export default EventDetailCard;
